/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

/** MUI imports */
import { styled } from "@mui/material/styles";
import { Box, Chip, Grid, Typography, Divider, FormControlLabel, Checkbox } from "@mui/material";

/** Component imports */
import { Onboarding, CleanUp, CatchUp, Recurring, Consulting } from "./";

/** Component styles */
const PREFIX = "AccountantRoot";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    chip: `${PREFIX}-chip`,
    chipProActive: `${PREFIX}-chipProActive`,
    chipProWasActive: `${PREFIX}-chipProWasActive`,
    selectedChip: `${PREFIX}-selectedChip`,
    selectedChipActive: `${PREFIX}-selectedChipActive`,
    selectedChipWasActive: `${PREFIX}-selectedChipWasActive`,
    divider: `${PREFIX}-divider`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 48px 48px",
    },

    [`& .${classes.chip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #ff5968",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.selectedChip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #031851",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.divider}`]: {
        marginBottom: 10,
    },
}));

export const AccountantRoot = (props) => {
    // Properties
    const {
        accountSystem,
        actives,
        clientMarket,
        companyID,
        expenseEst,
        serviceData,
        setalertType,
        setBackDrop,
        setServiceData,
        setShow,
        setTexto,
        setAuxExpanded,
        setExpanded,
        lastChipSelected,
        setLastChipSelected,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");
    const [renderState, setRenderState] = useState(false);

    /* Component states */
    const [valuesAcc, setValuesAcc] = useState({
        accountOnboard: false,
        accountCleanUp: false,
        accountCatchUp: false,
        accountantRecu: false,
        accountConsul: false,
    });
    const [projectsData, setProjectsData] = useState({
        accountOnboard: {},
        accountCleanUp: {},
        accountCatchUp: {},
        accountantRecu: {},
        accountConsul: {},
    });
    const [active, setActive] = useState({
        accountOnboard: {
            isActive: false,
            wasActive: false,
        },
        accountCleanUp: {
            isActive: false,
            wasActive: false,
        },
        accountCatchUp: {
            isActive: false,
            wasActive: false,
        },
        accountantRecu: {
            isActive: false,
            wasActive: false,
        },
        accountConsul: {
            isActive: false,
            wasActive: false,
        },
    });
    const [open, setOpen] = useState(false);
    const [infoAdd, setInfoAdd] = useState({});
    const [checkRemind, setCheckRemind] = useState(false);

    const handleCloseDialog = () => {
        setInfoAdd({});
        setCheckRemind(false);
        setOpen(false);
    };

    const showConfirm = (value) => {
        setInfoAdd(value);
        setOpen(true);
    };

    const handleChangeCheck = (event) => {
        setCheckRemind(event.target.checked);
    };

    const bodyMess = (
        <>
            <Typography variant="h1" color="primary" align="center" sx={{ fontSize: 18, pt: 3, pb: 2 }}>
                {t("miscellaneous.confRemoveCheckDet")}
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        name="checkedConf"
                        checked={checkRemind}
                        onChange={handleChangeCheck}
                        inputProps={{
                            "aria-label": "controlled",
                        }}
                    />
                }
                label={t("miscellaneous.dontShowAgain")}
            />
        </>
    );

    const getProjects = async (isReload) => {
        setBackDrop(true);

        let serviceCompID = serviceData._id ?? "";

        if (isReload) {
            await fetch(`/getNewServicesCompany?company_id=${companyID}`, {
                method: "GET",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
            })
                .then((res) => res.json())
                .then(async(data) => {
                    setServiceData(data);

                    let lastProject = data.filter(function (o) {
                        return o.hasOwnProperty("_id") && o.service_name === "Accountant";
                    })[0];

                    await fetch(
                        `/getProjectsCompany?company_id=${lastProject.company_id}&service_id=${lastProject.service_id}&service_company_id=${lastProject._id}`,
                        {
                            method: "GET",
                            headers: {
                                "Content-type": "application/json; charset=UTF-8",
                                "Access-Control-Allow-Origin": "*",
                            },
                        }
                    )
                        .then((res) => res.json())
                        .then(async(data) => {
                            let result = {};
                            let lastProyects = {
                                cleanUp: {},
                                catchUp: {},
                                recurring: {},
                                consulting: {},
                            };
                            let recurringisActive = false;
                            let wasRecurringActive = false;
        
                            await data.forEach((item) => {
                                switch (item.project_type) {
                                    case 1:
                                        result.accountOnboard = item;
                                        break;
                                    case 2:
                                        result.accountantRecu = item;
                                        if (item.projects_array.length !== 0) {
                                            let indexExact = 0;
                                            let dateMax = null;
                                            item.projects_array.forEach((item, index) => {
                                                if (index === 0) {
                                                    dateMax = item.end_date;
                                                } else {
                                                    if (moment(dateMax).isBefore(item.end_date)) {
                                                        dateMax = item.end_date;
                                                        indexExact = index;
                                                    }
                                                }
                                                if (!item.client_delivery_date && item.client_commitment_date) {
                                                    recurringisActive = true;
                                                } else if (item.client_delivery_date) {
                                                    wasRecurringActive = moment(item.client_delivery_date).isSameOrBefore(
                                                        moment()
                                                    );
                                                }
                                            });
                                            lastProyects.recurring = item.projects_array[indexExact];
                                            result.accountantRecu.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 3:
                                        result.accountCatchUp = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.catchUp = item.projects_array[0];
                                            result.accountCatchUp.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 4:
                                        result.accountCleanUp = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.cleanUp = item.projects_array[0];
                                            result.accountCleanUp.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 5:
                                        result.accountConsul = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.consulting = item.projects_array[0];
                                            result.accountConsul.project_company_id = item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            });
        
                            setProjectsData({
                                accountOnboard: result.accountOnboard,
                                accountCleanUp: result.accountCleanUp,
                                accountCatchUp: result.accountCatchUp,
                                accountantRecu: result.accountantRecu,
                                accountConsul: result.accountConsul,
                            });
        
                            setActive({
                                accountOnboard: {
                                    isActive:
                                        result.accountOnboard.since_date &&
                                        (!result.accountOnboard.end_date ||
                                            moment(result.accountOnboard.end_date).isAfter(moment())),
                                    wasActive: result.accountOnboard.end_date
                                        ? moment(result.accountOnboard.end_date).isSameOrBefore(moment())
                                        : false,
                                },
                                accountCleanUp: {
                                    isActive: lastProyects.cleanUp.since_date && !lastProyects.cleanUp.client_delivery_date,
                                    wasActive: lastProyects.cleanUp.client_delivery_date
                                        ? moment(lastProyects.cleanUp.client_delivery_date).isSameOrBefore(moment())
                                        : false,
                                },
                                accountCatchUp: {
                                    isActive: lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                                    wasActive: lastProyects.catchUp.client_delivery_date
                                        ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                        : false,
                                },
                                accountantRecu: {
                                    isActive: recurringisActive,
                                    wasActive: wasRecurringActive,
                                },
                                accountConsul: {
                                    isActive: false,
                                    wasActive: lastProyects.consulting.since_date ? true : false,
                                },
                            });

                            setAuxExpanded(lastProject.service_name);
                            setExpanded(true);
                            setRenderState(!renderState);
                        });
                });
        } else {
            await fetch(
                `/getProjectsCompany?company_id=${companyID}&service_id=${serviceData.service_id}&service_company_id=${serviceCompID}`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json; charset=UTF-8",
                        "Access-Control-Allow-Origin": "*",
                    },
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    let result = {};
                    let lastProyects = {
                        cleanUp: {},
                        catchUp: {},
                        recurring: {},
                        consulting: {},
                    };
                    let recurringisActive = false;
                    let wasRecurringActive = false;

                    data.forEach((item) => {
                        switch (item.project_type) {
                            case 1:
                                result.accountOnboard = item;
                                break;
                            case 2:
                                result.accountantRecu = item;
                                if (item.projects_array.length !== 0) {
                                    let indexExact = 0;
                                    let dateMax = null;
                                    item.projects_array.forEach((item, index) => {
                                        if (index === 0) {
                                            dateMax = item.end_date;
                                        } else {
                                            if (moment(dateMax).isBefore(item.end_date)) {
                                                dateMax = item.end_date;
                                                indexExact = index;
                                            }
                                        }
                                        if (!item.client_delivery_date && item.client_commitment_date) {
                                            recurringisActive = true;
                                        } else if (item.client_delivery_date) {
                                            wasRecurringActive = moment(item.client_delivery_date).isSameOrBefore(
                                                moment()
                                            );
                                        }
                                    });
                                    lastProyects.recurring = item.projects_array[indexExact];
                                    result.accountantRecu.project_company_id =
                                        item.projects_array[0].project_company_id;
                                }
                                break;
                            case 3:
                                result.accountCatchUp = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.catchUp = item.projects_array[0];
                                    result.accountCatchUp.project_company_id =
                                        item.projects_array[0].project_company_id;
                                }
                                break;
                            case 4:
                                result.accountCleanUp = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.cleanUp = item.projects_array[0];
                                    result.accountCleanUp.project_company_id =
                                        item.projects_array[0].project_company_id;
                                }
                                break;
                            case 5:
                                result.accountConsul = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.consulting = item.projects_array[0];
                                    result.accountConsul.project_company_id = item.projects_array[0].project_company_id;
                                }
                                break;
                            default:
                                break;
                        }
                    });

                    setProjectsData({
                        accountOnboard: result.accountOnboard,
                        accountCleanUp: result.accountCleanUp,
                        accountCatchUp: result.accountCatchUp,
                        accountantRecu: result.accountantRecu,
                        accountConsul: result.accountConsul,
                    });

                    setActive({
                        accountOnboard: {
                            isActive:
                                result.accountOnboard.since_date &&
                                (!result.accountOnboard.end_date ||
                                    moment(result.accountOnboard.end_date).isAfter(moment())),
                            wasActive: result.accountOnboard.end_date
                                ? moment(result.accountOnboard.end_date).isSameOrBefore(moment())
                                : false,
                        },
                        accountCleanUp: {
                            isActive: lastProyects.cleanUp.since_date && !lastProyects.cleanUp.client_delivery_date,
                            wasActive: lastProyects.cleanUp.client_delivery_date
                                ? moment(lastProyects.cleanUp.client_delivery_date).isSameOrBefore(moment())
                                : false,
                        },
                        accountCatchUp: {
                            isActive: lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                            wasActive: lastProyects.catchUp.client_delivery_date
                                ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                : false,
                        },
                        accountantRecu: {
                            isActive: recurringisActive,
                            wasActive: wasRecurringActive,
                        },
                        accountConsul: {
                            isActive: false,
                            wasActive: lastProyects.consulting.since_date ? true : false,
                        },
                    });
                });
        }

        setBackDrop(false);
    };

    /* Component functions */
    useEffect(() => {
        getProjects(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Component functions */
    useEffect(() => {
        if (lastChipSelected) {
            handleClickChip(valuesAcc[lastChipSelected], lastChipSelected, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderState]);

    const handleClickChip = async (value, option, setChip = false) => {
        switch (option) {
            case "accountOnboard":
                setValuesAcc({
                    ...valuesAcc,
                    accountOnboard: setChip ? !value : true,
                    accountCleanUp: false,
                    accountCatchUp: false,
                    accountantRecu: false,
                    accountConsul: false,
                });
                break;
            case "accountCleanUp":
                setValuesAcc({
                    ...valuesAcc,
                    accountCleanUp: setChip ? !value : true,
                    accountOnboard: false,
                    accountCatchUp: false,
                    accountantRecu: false,
                    accountConsul: false,
                });
                break;
            case "accountCatchUp":
                setValuesAcc({
                    ...valuesAcc,
                    accountCatchUp: setChip ? !value : true,
                    accountCleanUp: false,
                    accountOnboard: false,
                    accountantRecu: false,
                    accountConsul: false,
                });
                break;
            case "accountantRecu":
                setValuesAcc({
                    ...valuesAcc,
                    accountantRecu: setChip ? !value : true,
                    accountCatchUp: false,
                    accountCleanUp: false,
                    accountOnboard: false,
                    accountConsul: false,
                });
                break;
            default:
                setValuesAcc({
                    ...valuesAcc,
                    accountConsul: setChip ? !value : true,
                    accountantRecu: false,
                    accountCatchUp: false,
                    accountCleanUp: false,
                    accountOnboard: false,
                });
                break;
        }
        if (setChip) {
            setLastChipSelected(option);
        }
    };

    return (
        <StyledContainer>
            <Divider className={classes.divider} />
            <Grid container justifyContent="space-around" alignItems="center" spacing={2} sx={{ pb: 2 }}>
                <Grid item>
                    <Chip
                        id="onboarding-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.onboarding`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.accountOnboard
                                ? active.accountOnboard.isActive
                                    ? classes.selectedChipActive
                                    : active.accountOnboard.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.accountOnboard.isActive
                                ? classes.chipProActive
                                : active.accountOnboard.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.accountOnboard, "accountOnboard", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="clean-up-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.cleanUp`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.accountCleanUp
                                ? active.accountCleanUp.isActive
                                    ? classes.selectedChipActive
                                    : active.accountCleanUp.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.accountCleanUp.isActive
                                ? classes.chipProActive
                                : active.accountCleanUp.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.accountCleanUp, "accountCleanUp", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="catch-up-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.catchUp`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.accountCatchUp
                                ? active.accountCatchUp.isActive
                                    ? classes.selectedChipActive
                                    : active.accountCatchUp.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.accountCatchUp.isActive
                                ? classes.chipProActive
                                : active.accountCatchUp.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.accountCatchUp, "accountCatchUp", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="recurring-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.recurring`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.accountantRecu
                                ? active.accountantRecu.isActive
                                    ? classes.selectedChipActive
                                    : active.accountantRecu.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.accountantRecu.isActive
                                ? classes.chipProActive
                                : active.accountantRecu.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.accountantRecu, "accountantRecu", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="consulting-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.consulting`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.accountConsul
                                ? active.accountConsul.isActive
                                    ? classes.selectedChipActive
                                    : active.accountConsul.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.accountConsul.isActive
                                ? classes.chipProActive
                                : active.accountConsul.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.accountConsul, "accountConsul", true)}
                    />
                </Grid>
            </Grid>
            {valuesAcc.accountOnboard && (
                <Onboarding
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    actives={actives}
                    data={projectsData.accountOnboard}
                    serviceID={serviceData.service_id}
                    accountSystem={accountSystem}
                    expenseEst={expenseEst}
                />
            )}
            {valuesAcc.accountCleanUp && (
                <CleanUp
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    accountSystem={accountSystem}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    actives={actives}
                    data={projectsData.accountCleanUp}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.accountCatchUp && (
                <CatchUp
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    accountSystem={accountSystem}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    actives={actives}
                    data={projectsData.accountCatchUp}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.accountantRecu && (
                <Recurring
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    setBackDrop={setBackDrop}
                    actives={actives}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    accountSystem={accountSystem}
                    data={projectsData.accountantRecu}
                    reloadProjects={getProjects}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.accountConsul && (
                <Consulting
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    actives={actives}
                    data={projectsData.accountConsul}
                    serviceID={serviceData.service_id}
                />
            )}
        </StyledContainer>
    );
};
